import React, { useId, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguageDirection } from "@hooks/useLanguageDirection";

type Props = React.SVGProps<SVGSVGElement>;

const BASE_WIDTH = 115;
const HEIGHT = 58;

const BADGE_MARGIN_X = 12.5;
const BADGE_TOP_Y = 12.5;
const BADGE_BOTTOM_Y = 37.5;
const BADGE_CORNER_R = 4;

const CONTENT_ICON_W = 16;
const CONTENT_GAP = 2;
const CONTENT_PAD_X = 3.5;
const CONTENT_GROUP_Y = 26;

const TEXT_FONT_SIZE = 12;
const TEXT_FONT_WEIGHT = 700;
const TEXT_FONT_FAMILY = "system-ui, -apple-system, Segoe UI, Roboto, Arial";

function measureTextPx(text: string, font: string): number {
    if (typeof document === "undefined") {
        return 0;
    }
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return 0;
    }
    ctx.font = font;
    return ctx.measureText(text).width;
}

function createBadgePathD(width: number): string {
    const leftX = BADGE_MARGIN_X;
    const rightX = width - BADGE_MARGIN_X;
    const topY = BADGE_TOP_Y;
    const bottomY = BADGE_BOTTOM_Y;
    const r = BADGE_CORNER_R;

    const centerX = width / 2;


    const cornerCtrlDy = 1.7909;
    const cornerCtrlDx = 1.791;


    const tailAttachOffset = 9.8997;
    const tailCpFarOffset = 8.7657;
    const tailCpNearOffset = 7.685;
    const tailSlopeOffset = 6.9264;
    const tailTipOffset = 2.9732;
    const tailTipCpOffsetRight = 1.3843;
    const tailTipCpOffsetLeft = 1.3842;

    const tailCpNearY = 37.9813;
    const tailSlopeY = 38.8242;
    const tailTipY = 43.2168;
    const tailTipCpY = 44.9824;

    const xRightAttach = centerX + tailAttachOffset;
    const xRightCpFar = centerX + tailCpFarOffset;
    const xRightCpNear = centerX + tailCpNearOffset;
    const xRightSlope = centerX + tailSlopeOffset;
    const xTipRight = centerX + tailTipOffset;

    const xTipLeft = centerX - tailTipOffset;
    const xLeftSlope = centerX - tailSlopeOffset;
    const xLeftCpNear = centerX - tailCpNearOffset;
    const xLeftCpFar = centerX - tailCpFarOffset;
    const xLeftAttach = centerX - tailAttachOffset;

    const xTipCpRight = centerX + tailTipCpOffsetRight;
    const xTipCpLeft = centerX - tailTipCpOffsetLeft;

    return [
        `M${rightX} ${bottomY - r}`,
        `C${rightX} ${bottomY - cornerCtrlDy} ${rightX - cornerCtrlDx} ${bottomY} ${rightX - r} ${bottomY}`,
        `H${xRightAttach}`,
        `C${xRightCpFar} ${bottomY} ${xRightCpNear} ${tailCpNearY} ${xRightSlope} ${tailSlopeY}`,
        `L${xTipRight} ${tailTipY}`,
        `C${xTipCpRight} ${tailTipCpY} ${xTipCpLeft} ${tailTipCpY} ${xTipLeft} ${tailTipY}`,
        `L${xLeftSlope} ${tailSlopeY}`,
        `C${xLeftCpNear} ${tailCpNearY} ${xLeftCpFar} ${bottomY} ${xLeftAttach} ${bottomY}`,
        `H${leftX + r}`,
        `C${leftX + cornerCtrlDy} ${bottomY} ${leftX} ${bottomY - cornerCtrlDx} ${leftX} ${bottomY - r}`,
        `V${topY + r}`,
        `C${leftX} ${topY + cornerCtrlDy} ${leftX + cornerCtrlDy} ${topY} ${leftX + r} ${topY}`,
        `H${rightX - r}`,
        `C${rightX - cornerCtrlDx} ${topY} ${rightX} ${topY + cornerCtrlDy} ${rightX} ${topY + r}`,
        `V${bottomY - r}`,
        "Z",
    ].join("");
}

export default function ShimmerBadge(props: Props) {
    const uid = useId().replace(/:/g, "");
    const { t, i18n } = useTranslation();
    const langDir = useLanguageDirection();
    const isRTL = langDir === "rtl";
    const shimmerMaskId = `shape-mask-${uid}`;
    const shimmerCoreGradId = `shimmer-core-${uid}`;
    const shimmerGlowGradId = `shimmer-glow-${uid}`;
    const shimmerGlowBlurId = `shimmer-glow-blur-${uid}`;

    const label = useMemo(() => t("free gift").toLocaleUpperCase(i18n.language), [t, i18n.language]);
    const [measuredTextW, setMeasuredTextW] = useState<number | null>(null);

    useLayoutEffect(() => {
        const font = `${TEXT_FONT_WEIGHT} ${TEXT_FONT_SIZE}px ${TEXT_FONT_FAMILY}`;
        setMeasuredTextW(measureTextPx(label, font));
    }, [label]);

    const badgeWidth = useMemo(() => {
        if (measuredTextW == null) return BASE_WIDTH;
        const contentWidth = CONTENT_ICON_W + CONTENT_GAP + Math.ceil(measuredTextW);
        const innerWidth = 2 * CONTENT_PAD_X + contentWidth;
        return BADGE_MARGIN_X * 2 + innerWidth;
    }, [measuredTextW]);

    const badgePathD = useMemo(() => createBadgePathD(badgeWidth), [badgeWidth]);

    const badgeOuterRectX = BADGE_MARGIN_X - 1;
    const badgeOuterRectY = BADGE_TOP_Y - 1;
    const badgeOuterRectW = badgeWidth - (2 * BADGE_MARGIN_X) + 2;
    const badgeOuterRectH = 34;

    const centerX = badgeWidth / 2;
    const paint0X = centerX + 20.1852;
    const paint1X1 = centerX + 16.6296;
    const paint1X2 = centerX + 17.4239;

    const iconX = isRTL ? 24 : -40;
    const textX = isRTL ? 22 : -22;
    const contentGroupX = isRTL ? badgeWidth - 56 : 56;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={badgeWidth}
            height={HEIGHT}
            viewBox={`0 0 ${badgeWidth} ${HEIGHT}`}
            fill="none"
            {...props}
        >

            <mask
                id={`path-1-outside-1_2956_13460-${uid}`}
                maskUnits="userSpaceOnUse"
                x={badgeOuterRectX}
                y={badgeOuterRectY}
                width={badgeOuterRectW}
                height={34}
                fill="black"
            >
                <rect fill="white" x={badgeOuterRectX} y={badgeOuterRectY} width={badgeOuterRectW} height={badgeOuterRectH} />
                <path d={badgePathD} />
            </mask>


            <path
                d={badgePathD}
                fill="#851111"
            />

            <path
                d={badgePathD}
                fill="none"
                stroke="#851111"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                mask={`url(#path-1-outside-1_2956_13460-${uid})`}
            />


            <g opacity={0.56} filter={`url(#filter0_f_2956_13460-${uid})`}>
                <mask
                    id={`path-3-outside-2_2956_13460-${uid}`}
                    maskUnits="userSpaceOnUse"
                    x={badgeOuterRectX}
                    y={badgeOuterRectY}
                    width={badgeOuterRectW}
                    height={34}
                    fill="black"
                >
                    <rect fill="white" x={badgeOuterRectX} y={badgeOuterRectY} width={badgeOuterRectW} height={badgeOuterRectH} />
                    <path d={badgePathD} />
                </mask>

                <path
                    d={badgePathD}
                    fill="#851111"
                />

                <path
                    d={badgePathD}
                    fill="none"
                    stroke="#FFB0B0"
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    mask={`url(#path-3-outside-2_2956_13460-${uid})`}
                />
            </g>

            <g filter={`url(#filter1_di_2956_13460-${uid})`}>
                <mask
                    id={`path-5-outside-3_2956_13460-${uid}`}
                    maskUnits="userSpaceOnUse"
                    x={badgeOuterRectX}
                    y={badgeOuterRectY}
                    width={badgeOuterRectW}
                    height={34}
                    fill="black"
                >
                    <rect fill="white" x={badgeOuterRectX} y={badgeOuterRectY} width={badgeOuterRectW} height={badgeOuterRectH} />
                    <path d={badgePathD} />
                </mask>

                <path
                    d={badgePathD}
                    fill={`url(#paint0_linear_2956_13460-${uid})`}
                    shapeRendering="crispEdges"
                />

                <path
                    d={badgePathD}
                    fill="none"
                    stroke={`url(#paint1_linear_2956_13460-${uid})`}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    style={{ mixBlendMode: "plus-lighter" }}
                    mask={`url(#path-5-outside-3_2956_13460-${uid})`}
                />
            </g>


            <rect
                width={badgeWidth}
                height={HEIGHT}
                fill={`url(#${shimmerGlowGradId})`}
                mask={`url(#${shimmerMaskId})`}
                filter={`url(#${shimmerGlowBlurId})`}
                opacity={0.9}
                style={{ mixBlendMode: "plus-lighter" }}
            />

            <rect
                width={badgeWidth}
                height={HEIGHT}
                fill={`url(#${shimmerCoreGradId})`}
                mask={`url(#${shimmerMaskId})`}
                opacity={0.85}
                style={{ mixBlendMode: "plus-lighter" }}
            />


            <defs>

                <filter
                    id={`filter0_f_2956_13460-${uid}`}
                    x={0}
                    y={0}
                    width={badgeWidth}
                    height={57.041}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity={0} result="BackgroundImageFix" />
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                    <feGaussianBlur stdDeviation={6} result="effect1_foregroundBlur_2956_13460" />
                </filter>

                <filter
                    id={`filter1_di_2956_13460-${uid}`}
                    x={6}
                    y={10}
                    width={badgeWidth - 12}
                    height={45.041}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity={0} result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy={4} />
                    <feGaussianBlur stdDeviation={3} />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2956_13460" />
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2956_13460" result="shape" />

                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy={2} />
                    <feGaussianBlur stdDeviation={1.5} />
                    <feComposite in2="hardAlpha" operator="arithmetic" k2={-1} k3={1} />
                    <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 1 0 0 0 0 0.725 0 0 0 0 0.725 0 0 0 0.78 0"
                    />
                    <feBlend mode="normal" in2="shape" result="effect2_innerShadow_2956_13460" />
                </filter>

                <linearGradient
                    id={`paint0_linear_2956_13460-${uid}`}
                    x1={paint0X}
                    y1={19.6329}
                    x2={paint0X}
                    y2={40.9587}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#B91C1C" />
                    <stop offset={1} stopColor="#4B0505" />
                </linearGradient>

                <linearGradient
                    id={`paint1_linear_2956_13460-${uid}`}
                    x1={paint1X1}
                    y1={10.9999}
                    x2={paint1X2}
                    y2={43.6912}
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#FF6161" />
                    <stop offset={1} stopColor="#6E0D0D" stopOpacity={0} />
                </linearGradient>

                <mask id={shimmerMaskId}>
                    <rect width="100%" height="100%" fill="black" />

                    <path
                        d={badgePathD}
                        fill="white"
                    />
                </mask>

                <linearGradient
                    id={shimmerGlowGradId}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                    gradientTransform="rotate(5 0.5 0.5)"
                >
                    <stop offset="0%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="30%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="44%" stopColor="#fff" stopOpacity="0.08" />
                    <stop offset="50%" stopColor="#fff" stopOpacity="0.32" />
                    <stop offset="56%" stopColor="#fff" stopOpacity="0.08" />
                    <stop offset="70%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="100%" stopColor="#fff" stopOpacity="0" />

                    <animateTransform
                        attributeName="gradientTransform"
                        additive="sum"
                        type="translate"
                        dur="4.6s"
                        repeatCount="indefinite"
                        values="1.2 0; -1.2 0; -1.2 0; 1.2 0"
                        keyTimes="0;0.4;0.6;1"
                    />
                </linearGradient>

                <linearGradient
                    id={shimmerCoreGradId}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                    gradientTransform="rotate(5 0.5 0.5)"
                >
                    <stop offset="0%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="40%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="46%" stopColor="#fff" stopOpacity="0.18" />
                    <stop offset="50%" stopColor="#fff" stopOpacity="0.92" />
                    <stop offset="54%" stopColor="#fff" stopOpacity="0.18" />
                    <stop offset="60%" stopColor="#fff" stopOpacity="0" />
                    <stop offset="100%" stopColor="#fff" stopOpacity="0" />

                    <animateTransform
                        attributeName="gradientTransform"
                        additive="sum"
                        type="translate"
                        dur="4.6s"
                        repeatCount="indefinite"
                        values="1.2 0; -1.2 0; -1.2 0; 1.2 0"
                        keyTimes="0;0.4;0.6;1"
                    />
                </linearGradient>

                <filter id={shimmerGlowBlurId} x="-70%" y="-70%" width="240%" height="240%">
                    <feGaussianBlur stdDeviation={5.5} />
                </filter>
            </defs>
            <g pointerEvents="none">
                <g transform={`translate(${contentGroupX}, ${CONTENT_GROUP_Y})`}>
                    <image
                        href="/images/icon-gift-box.png"
                        x={iconX}
                        y="-9.5"
                        width={16}
                        height={16}
                    >
                        <title>Gift box icon</title>
                    </image>
                    <text
                        x={textX}
                        y="0"
                        textAnchor="start"
                        dominantBaseline="middle"
                        direction={isRTL ? "rtl" : "ltr"}
                        unicodeBidi="plaintext"
                        fill="#F8CC8B"
                        fontSize="12"
                        fontFamily={TEXT_FONT_FAMILY}
                        fontWeight={TEXT_FONT_WEIGHT}
                    >
                        {label}
                    </text>
                </g>
            </g>
        </svg>
    );
}
